import { BfaConstants, Country, LocaleConstants } from '@ddt-web/domain';
import * as isNaN from 'lodash/isNaN';
import * as isNil from 'lodash/isNil';
import * as trim from 'lodash/trim';

export class NumberUtil {
	private static readonly CURRENCY_ARRAY = ['$', '£'];
	private static readonly LOCALE_EN_US = 'en-US';

	public static numberFormatter(num: number, formatter: string = ''): string {
		return isNaN(num) || isNil(num) ? '--' : `${num.toFixed(2)}${formatter}`;
	}

	public static commaAndDecimalNumberFormatter(num: string | number): string {
		const numericParts = num.toString().split('.');
		numericParts[0] = numericParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		return numericParts.join('.');
	}

	/**
	 * Checks if the provided string value is a numerical value.
	 * If allowPercentage is true, checks if the string ends with '%'.
	 * If allowCurrency is true, checks if the string starts with any of the CURRENCY_ARRAY values.
	 *
	 * @param stringValue string value to check if it is numerical
	 * @param allowPercentage default true
	 * @param allowCurrency default true
	 * @returns true if value is numerical, false otherwise
	 */
	public static isNumericalValue(stringValue: string, allowPercentage: boolean = true, allowCurrency: boolean = true): boolean {
		let stringValueToCheck = trim(stringValue);

		if (stringValueToCheck) {
			if (allowPercentage && stringValueToCheck.endsWith('%')) {
				stringValueToCheck = stringValueToCheck.substring(0, stringValueToCheck.length - 1);
			}

			if (allowCurrency) {
				NumberUtil.CURRENCY_ARRAY.forEach(currencyString => {
					if (stringValueToCheck.startsWith(currencyString)) {
						stringValueToCheck = stringValueToCheck.substring(1);
					}
				});
			}

		}

		return !isNaN(parseFloat(stringValueToCheck)) && isFinite(stringValueToCheck);
	}

	/**
	 * Used when a negative number is provided in brackets.
	 * Example: 1234 is positive, (1234) is -1234
	 */
	public static getNumberFromString(stringValue: string, defaultValue: number = null): number {
		const negativeMultiplier = stringValue.indexOf('(') > 0 || stringValue.indexOf(')') > 0 ? -1 : 1;
		const cleanedString = stringValue.replace(/[^0-9\.-]+/g, '');
		if (cleanedString === '') {
			return defaultValue;
		}

		const value = Number(cleanedString);
		if (isNaN(value) && defaultValue !== null) {
			return defaultValue;
		} else {
			return value * negativeMultiplier;
		}
	}

	/**
	 * Method to parse number strings based on country (locale)
	 * @param numberString
	 * @param country works with different formats of country / locale code such as en_US, en-US, en-us and en_us
	 * @param defaultValue
	 * @returns number
	 */
	public static parseNumbersWithLocale(numberString: string, country: string = 'en_US', defaultValue?: number, delimiter?: string): number {
		// if delimiter not specified, determine from locale
		const decimalSeparator = delimiter ?? this.delimiterFromLocale(country);
		numberString = numberString.replace(new RegExp(`[^${decimalSeparator}0-9-]`, 'g'), '').replace(new RegExp(`[${decimalSeparator}]`, 'g'), '.');
		return numberString ? +numberString : defaultValue ?? NaN;
	}

	private static delimiterFromLocale(country: string = 'en_US'): string {
		return LocaleConstants.COMMA_DECIMAL_SEPARATOR_COUNTRIES.map(data => data.toLowerCase()).includes(country?.replace(/\-/g, '_').toLowerCase() as Country) ? ',' : '.';
	}

	/**
	 * *
	 * Formats a number with the specified amount of decimal places.
	 * Defaults to BfaConstants.DECIMAL_PLACES.
	 * @ param {number} value the value which needs formatting.
	 * @ param {number} decimals (optional) the number of decimal places required - defaults to BfaConstants.DECIMAL_PLACES.
	 * @ param {boolean} strict (optional) show exactly the specified number of decimal places, even if value is round number
	 * 						    e.g. formatNumber(3.10, 2, true) => "3.10"
	 *                                 formatNumber(3.10, 2, false) => "3.1"
	 * @ param {boolean} useAccountingFormat (optional) true to surround negative numbers with brackets and remove sign
	 *                            e.g. "-3.10" becomes "(3.10)"
	 * @ returns {number} the value formatted to the specified number of decimal places, or NaN if the value is invalid.
	 *
	 */
	public static formatNumber(value: number, decimals: number = BfaConstants.DEFAULT_DECIMAL_PLACES, strict: boolean = true, useAccountingFormat: boolean = false): string {
		if (!isFinite(value)) {
			return '' + NaN;
		}

		let valueStr: string = value.toLocaleString(NumberUtil.LOCALE_EN_US, {
			maximumFractionDigits: decimals,
			minimumFractionDigits: decimals
		});

		// show round numbers if possible if strict === false
		if (!strict) {
			valueStr = '' + parseFloat(valueStr.replace(/,/g, ''));
		}
		valueStr = useAccountingFormat && value < 0 ? '(' + valueStr.slice(1) + ')' : valueStr; // format negative numbers with brackets (e.g. -3 => (3))

		return valueStr;
	}

	public static precisionRound(number: number, precision: number): number {
		const factor = Math.pow(10, precision);
		return Math.round((number + Number.EPSILON) * factor) / factor;
	}

	public static formatWithPrecisionRounding(number: number, precision: number, decimals: number = BfaConstants.DEFAULT_DECIMAL_PLACES): string {
		const roundNumber = this.precisionRound(number, precision);
		return roundNumber.toLocaleString(NumberUtil.LOCALE_EN_US, {
			maximumFractionDigits: decimals,
			minimumFractionDigits: decimals
		});
	}

	/**
	 * Formats a number with the specified amount of decimal places. See formatNumber(value, decimals, strict, useAccountingFormat)
	 *
	 * This method handle a special case where if the formatted value is negative zero (e.g. -0.00) then it format it as 0.00.
	 */
	public static formatAndConvertNegativeZero(value: number, decimals: number = BfaConstants.DEFAULT_DECIMAL_PLACES, strict: boolean = true): string {
		if (!isFinite(value) || isNil(value)) {
			return '' + NaN;
		}
		// calling Number(-0.00) will convert to just 0
		let numValue = Number(value.toFixed(decimals));
		// then format 0 to 0.00

		// Remove negative zeros
		if (Object.is(numValue, -0)) {
			numValue = 0;
		}

		return this.formatNumber(numValue, decimals, strict);
	}

	public static isEpsilon(num: number): boolean {
		return Math.abs(num) < 1e-1;
	}
}
